/**
 * ④PJ の変更一覧の組み立て — 案件の変更を、6 レーン（要求 │ ステージ │ 業務フロー │ 業務 │
 * 業務ルール │ 画面）に出す中身へ畳む。
 *
 * **出すのは「この案件が触った要素」だけである**（ラフ `pj.html`）。業務全体像が全件を出す
 * のに対し、ここは案件の変更の行に出てくる要素だけに絞る。レーン列そのもの（`LaneBoard`）は
 * 業務全体像と同じものを使う — 仕様を見る目と案件を見る目が、画面を移った瞬間に切り替わらずに
 * 済む。
 *
 * **削除された要素は「ここに出していない N 件」で済ませない。** 案件が消した要素は案件を
 * 当てた姿（`after`）に居ないので、master の姿から拾って削除の印を付けたカードとして出す。
 * 件数だけの注記にすると、何が消えるのかが画面から読めない。そのために業務全体像の読取を
 * **案件ありと master の 2 本**引く（設計書 `api.html#a7`）。
 *
 * **印の出どころは案件の変更の読取 1 本に絞る**（決定 7）。レーンに出す要素と印は、その読取が
 * 返す `laneChanges`（共通部が案件の変更の行と階層のエッジから決めた、直接の追加 / 変更 / 削除と
 * 配下に変更）から、**直接の変更だけ**を残して使う（`withoutDescendantPjLaneChanges`。Dhik 2026-09-29「PJの
 * 変更一覧で出すものをchangeに紐付けられた変更のみとする」）。ここは型ごとに束ね直すだけで、直接か
 * 配下かの判定そのものはしない。画面のレーンも同じで、部品の変更を持ち主の画面へ寄せる判定と、
 * 画面が操作する業務・属するステージの導出は、読取の `screenChanges` が持つ（共通部の
 * `buildPjScreenChanges`）。ここは絞り込みを当てるだけである。
 *
 * **レーンに載らない変更（用語・マスター）は拾わない**（Dhik 2026-09-29「PJ変更一覧の横断する変更の情報は
 * 丸ごと消してほしい」）。前は差分の読取（`getPjDiff`）の項目から「横断的に関わるデータ」として拾っていた。
 *
 * **業務全体像の読取の形は F1 の持ち物である。** その読取は第 1 段・第 2 段を旧い欄名で返すので、
 * 画面の語（ステージ・業務フロー）への読み替えは `laneViewOf` の 1 箇所だけで行う。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が直接変えた要素と変わった画面だけの 6 レーンの中身と絞り込みを組み立てる。
 * @graph-connects none
 */
import type { PjLaneChange, PjRequirement, PjScreenChange } from '@cortex/domain-spec-console';

import type { SpecDiffState } from '../components/spec/SpecDiffState.js';
import type {
  LaneBusiness,
  LaneFlow,
  LaneRule,
  LaneSnapshot,
  LaneStage,
} from '../lib/spec-graph-types/lanes.js';
import { isDirectPjLaneMark, pjCardMarkOf, type PjLaneChangeKind } from './pj-lane-marks.js';

/**
 * 階層のレーンに出す要素の型 4 種。並びはレーンの並び（左から右）に合わせる。
 *
 * 画面と部品は右端の画面のレーン（`PjScreenItem`）に出し、用語とマスター（アクター・ドメイン・動詞・
 * 単位）はこの画面に出さない。4 段の階層に載らない型を階層のレーンへ混ぜると、左から右への絞り込みが
 * どの段にも効かないカードが並ぶ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧のレーンに出す要素の型 4 種の閉集合
 * @graph-connects none
 */
export const PJ_LANE_ELEMENT_TYPES = ['Stage', 'Flow', 'Business', 'Rule'] as const;

/**
 * レーンに出す要素の型。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧のレーンに出す要素の型
 * @graph-connects none
 */
export type PjLaneElementType = (typeof PJ_LANE_ELEMENT_TYPES)[number];

/**
 * 画面のレーンの 1 件。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面のレーンの 1 件（id・名前・未翻訳・印・変わった部品・部品が操作する業務・属するステージ）
 * @graph-connects none
 */
export interface PjScreenItem {
  readonly screenId: number;
  readonly label: string;
  readonly untranslated: boolean;
  /** カードの印。画面自身の追加・変更・削除で、部品だけが変わった画面は変更なし（配下に変更は出さない） */
  readonly mark: SpecDiffState;
  /** 案件で変わった、この画面の部品の id */
  readonly componentIds: readonly number[];
  /** この画面の部品が操作する業務の id */
  readonly businessIds: readonly number[];
  /** この画面が属するステージの id */
  readonly stageIds: readonly number[];
}

/**
 * 画面の名前 1 件（画面一覧の読取の行から要る欄だけ）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の名前 1 件（id・名前・未翻訳）
 * @graph-connects none
 */
export interface PjScreenLabel {
  readonly screenId: number;
  readonly label: string;
  readonly untranslated: boolean;
}

/**
 * 案件で変わった画面に名前を付けて、画面のレーンの 1 件にする。
 *
 * 名前は案件を当てた姿の画面一覧から、そこに居なければ（案件が消した画面）master の画面一覧から
 * 引く。どちらにも居なければ空の名前にして、見出しは「未設定」になる（id に落とさない）。
 *
 * **部品だけが変わった画面（読取の `descendant`）もレーンには出すが、印は付けない**（Dhik 2026-09-29
 * 「PJモードの業務マップでも配下に変更を出さず、色もつけないでほしい」の「でも」＝業務マップ以外も）。
 * 画面自身の追加・変更・削除だけが札と地色を持つ。
 *
 * @param changes 案件で変わった画面（読取の `screenChanges`）
 * @param after 案件を当てた姿の画面一覧
 * @param master master の画面一覧
 * @returns 画面のレーンの中身（読取の並び）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わった画面に、案件を当てた姿か master の画面一覧から名前を付け、画面自身の追加・変更・削除だけを印にする。
 * @graph-connects pjCardMarkOf [calls] 読取の変わり方をカードの印にする
 */
export function pjScreenItemsOf(
  changes: readonly PjScreenChange[],
  after: readonly PjScreenLabel[],
  master: readonly PjScreenLabel[],
): readonly PjScreenItem[] {
  const labels = new Map<number, PjScreenLabel>();
  for (const screen of [...master, ...after]) labels.set(screen.screenId, screen);
  return changes.map((change) => {
    const named = labels.get(change.screenId);
    return {
      screenId: change.screenId,
      label: named?.label ?? '',
      untranslated: named?.untranslated ?? false,
      mark: pjCardMarkOf(change.changeKind),
      componentIds: change.componentIds,
      businessIds: change.businessIds,
      stageIds: change.stageIds,
    };
  });
}

/**
 * 共通部が決めた変わったレーン 4 型から、直接の変更（追加 / 変更 / 削除）だけを残す。
 *
 * **4 レーンは案件の変更の行を持つ要素だけを出す**（Dhik 2026-09-29「PJの変更一覧で出すものを
 * changeに紐付けられた変更のみとする」）。配下に変更（`descendant`）は、自身の変更の行が無く配下が
 * 変わっただけの印なので外す。レーンの中身（`buildPjTouchedByType`）とカードの印の索引の両方をここを
 * 通した値から作る — 片方だけ絞ると、出ないカードの印や、印の無いカードが生まれる。画面のレーン
 * （`screenChanges`）はここを通さない。
 *
 * **業務マップの印の索引もここを通す**（Dhik 2026-09-29「PJモードの業務マップでも配下に変更を出さず、
 * 色もつけないでほしい」）。業務マップは全件を出すのでレーンの中身は絞らず、印だけを直接の変更に絞る。
 * 同じ関数を通すので、2 つの画面で印を持つ要素が食い違わない。
 *
 * @param laneChanges 共通部が決めた変わったノード（直接と配下）
 * @returns 直接の変更だけ（読取の並びのまま）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わったレーン 4 型から、配下に変更だけのノードを外し、案件の変更の行を持つノードだけを残す。
 * @graph-connects isDirectPjLaneMark [calls] 印が直接の変更かの判定
 */
export function withoutDescendantPjLaneChanges(
  laneChanges: readonly PjLaneChange[],
): readonly PjLaneChange[] {
  return laneChanges.filter((change) => isDirectPjLaneMark(change.changeKind));
}

/**
 * 要素の型から、その型で変わったノードの id と変わり方を引く索引。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わったノードを、要素の型ごとの（id → 変わり方）の索引へ束ねたもの
 * @graph-connects none
 */
export type PjTouchedByType = ReadonlyMap<string, ReadonlyMap<number, PjLaneChangeKind>>;

/**
 * 変わったレーン 4 型を、型ごとの索引へ束ねる。
 *
 * **ここで階層を辿らない。** 直接か配下かの判定は共通部の `propagatePjLaneChanges` 1 箇所が
 * 持ち、`getChangeHistory` の `laneChanges` として返る。画面は束ね直すだけである。
 *
 * @param laneChanges 変わったノード（④PJ の変更一覧は `withoutDescendantPjLaneChanges` を通した直接の変更だけ）
 * @returns 型ごとの（id → 変わり方）の索引
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変わったレーン 4 型を、要素の型ごとの（id → 変わり方）の索引へ束ねる。
 * @graph-connects none
 */
export function buildPjTouchedByType(laneChanges: readonly PjLaneChange[]): PjTouchedByType {
  const index = new Map<string, Map<number, PjLaneChangeKind>>();
  for (const change of laneChanges) {
    const byId = index.get(change.nodeType) ?? new Map<number, PjLaneChangeKind>();
    byId.set(change.nodeId, change.changeKind);
    index.set(change.nodeType, byId);
  }
  return index;
}

/**
 * ある型で変わったノードの id を、共通部が返した順（id の昇順）で返す。
 *
 * @param touched 型ごとの索引
 * @param elementType 要素の型
 * @returns 変わったノードの id
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ある型で案件が変えたノードの id を取り出す。
 * @graph-connects none
 */
export function pjTouchedIdsOf(
  touched: PjTouchedByType,
  elementType: PjLaneElementType,
): ReadonlySet<number> {
  return new Set(touched.get(elementType)?.keys() ?? []);
}

/**
 * ステージレーンの 1 件（画面の語に読み替えたもの）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージ 1 件（id・名前・未翻訳・配下の業務フロー）
 * @graph-connects none
 */
export interface PjStageItem {
  readonly stageId: number;
  readonly label: string;
  readonly untranslated: LaneStage['untranslated'];
  readonly flowIds: readonly number[];
}

/**
 * 業務フローレーンの 1 件（画面の語に読み替えたもの）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 件（id・属するステージ・名前・未翻訳・型・含む業務）
 * @graph-connects none
 */
export interface PjFlowItem {
  readonly flowId: number;
  readonly stageId: number | null;
  readonly label: string;
  readonly untranslated: LaneFlow['untranslated'];
  /** 業務フローの型。業務マップと同じ並び（メインを一番上）と札に使う。読めなければ null */
  readonly role: LaneFlow['role'];
  readonly businessIds: readonly number[];
}

/**
 * 4 レーンの姿を画面の語で持ち直したもの。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージ・業務フロー・業務・業務ルールの平らな 4 配列
 * @graph-connects none
 */
export interface PjLaneView {
  readonly stages: readonly PjStageItem[];
  readonly flows: readonly PjFlowItem[];
  readonly businesses: readonly LaneBusiness[];
  readonly rules: readonly LaneRule[];
}

/**
 * 業務全体像の読取の姿を、画面の語（ステージ・業務フロー）へ読み替える。
 *
 * **読み替えはここ 1 箇所だけで行う。** 読取の欄名は業務マップ（F1）の持ち物で、F4 は形を
 * 変えない。業務マップが 4 配列（＋導出の鎖）になったので、第 1 段・第 2 段は `stages` / `flows` を読む。
 *
 * @param snapshot 業務全体像の読取の姿
 * @returns 画面の語で持ち直した 4 配列
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務全体像の読取の第 1 段・第 2 段を、ステージ・業務フローの語へ読み替える。
 * @graph-connects none
 */
export function laneViewOf(snapshot: LaneSnapshot): PjLaneView {
  return {
    stages: snapshot.stages.map((row) => ({
      stageId: row.stageId,
      label: row.label,
      untranslated: row.untranslated,
      flowIds: row.flowIds,
    })),
    flows: snapshot.flows.map((row) => ({
      flowId: row.flowId,
      stageId: row.stageId,
      label: row.label,
      untranslated: row.untranslated,
      role: row.role,
      businessIds: row.businessIds,
    })),
    businesses: snapshot.businesses,
    rules: snapshot.rules,
  };
}

/**
 * 絞り込みの選択を持つレーン 4 種。業務ルールと画面のカードは絞る先が無いので選択を持たない
 * （業務ルールのカードは押すと業務ルールの画面へ移る。画面のカードは押せない）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧で押せるレーン 4 種の閉集合
 * @graph-connects none
 */
export const PJ_LANES_SELECTION_LANES = ['requirement', 'stage', 'flow', 'business'] as const;

/**
 * 押せるレーンの種別。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧で押せるレーンの種別
 * @graph-connects none
 */
export type PjLanesSelectionLane = (typeof PJ_LANES_SELECTION_LANES)[number];

/**
 * いま選ばれているカード。
 *
 * 要求だけが**横断の条件**で、ステージ → 業務フロー → 業務 の左右関係の外に立つ。要求を選んでも
 * 階層の選択は落ちず、階層を選んでも要求は残る（ラフ `pj.html` の `byReq`）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧でいま選ばれているカード（要求と、階層 3 レーンの id）
 * @graph-connects none
 */
export interface PjLanesSelection {
  /** 選んでいる要求の id */
  readonly requirementId?: number;
  /** 選んでいるステージの id */
  readonly stageId?: number;
  /** 選んでいる業務フローの id */
  readonly flowId?: number;
  /** 選んでいる業務の id */
  readonly businessId?: number;
}

/**
 * 何も選んでいない状態。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧で何も選んでいない状態
 * @graph-connects none
 */
export const EMPTY_PJ_LANES_SELECTION: PjLanesSelection = {};

/**
 * 選択の欄のうち値の在るものだけで選択を組む。`undefined` の欄を実体に残さない。
 *
 * @param parts 欄ごとの値
 * @returns 値の在る欄だけの選択
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 値の在る欄だけで選択を組み直す。
 * @graph-connects none
 */
function selectionOf(parts: {
  requirementId: number | undefined;
  stageId: number | undefined;
  flowId: number | undefined;
  businessId: number | undefined;
}): PjLanesSelection {
  const next: {
    requirementId?: number;
    stageId?: number;
    flowId?: number;
    businessId?: number;
  } = {};
  if (parts.requirementId !== undefined) next.requirementId = parts.requirementId;
  if (parts.stageId !== undefined) next.stageId = parts.stageId;
  if (parts.flowId !== undefined) next.flowId = parts.flowId;
  if (parts.businessId !== undefined) next.businessId = parts.businessId;
  return next;
}

/**
 * カードを押したときの新しい選択を返す。
 *
 * 階層は業務全体像と同じ進み方である — 上位を選び直すと下位の選択は外れ、同じカードの再押下で
 * 外れる。業務フローを選ぶとそのステージも立つ。要求は横断の条件なので、押すと立ち、もう一度
 * 押すと外れるだけで、階層の選択には触らない。
 *
 * @param current いまの選択
 * @param lane 押したカードのレーン
 * @param id 押したカードの id
 * @param stageOfFlow 業務フローの id から属するステージを引く表
 * @returns 押したあとの選択
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business カードの押下から次の選択を決める（要求は横断、階層は左を選び直すと右が落ちる）。
 * @graph-connects none
 */
export function togglePjLanesSelection(
  current: PjLanesSelection,
  lane: PjLanesSelectionLane,
  id: number,
  stageOfFlow: ReadonlyMap<number, number | null>,
): PjLanesSelection {
  const requirementId = current.requirementId;
  if (lane === 'requirement') {
    return selectionOf({
      ...current,
      requirementId: requirementId === id ? undefined : id,
      stageId: current.stageId,
      flowId: current.flowId,
      businessId: current.businessId,
    });
  }
  if (lane === 'stage') {
    return current.stageId === id
      ? clearPjLanesSelection(current, 'stage')
      : selectionOf({ requirementId, stageId: id, flowId: undefined, businessId: undefined });
  }
  if (lane === 'flow') {
    return current.flowId === id
      ? clearPjLanesSelection(current, 'flow')
      : selectionOf({
          requirementId,
          stageId: stageOfFlow.get(id) ?? undefined,
          flowId: id,
          businessId: undefined,
        });
  }
  return current.businessId === id
    ? clearPjLanesSelection(current, 'business')
    : selectionOf({
        requirementId,
        stageId: current.stageId,
        flowId: current.flowId,
        businessId: id,
      });
}

/**
 * 1 つのレーンの選択を外す。階層のレーンは、そのレーンより右の選択も一緒に落とす。
 *
 * @param current いまの選択
 * @param lane 外すレーン
 * @returns 外したあとの選択
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business レーン 1 つの選択を外す（要求は単独、階層はそれより右も落とす）。
 * @graph-connects none
 */
export function clearPjLanesSelection(
  current: PjLanesSelection,
  lane: PjLanesSelectionLane,
): PjLanesSelection {
  const { requirementId, stageId, flowId } = current;
  if (lane === 'requirement') {
    return selectionOf({
      ...current,
      requirementId: undefined,
      stageId,
      flowId,
      businessId: current.businessId,
    });
  }
  if (lane === 'stage') {
    return selectionOf({
      requirementId,
      stageId: undefined,
      flowId: undefined,
      businessId: undefined,
    });
  }
  if (lane === 'flow') {
    return selectionOf({ requirementId, stageId, flowId: undefined, businessId: undefined });
  }
  return selectionOf({ requirementId, stageId, flowId, businessId: undefined });
}

/**
 * 選択に応じて 5 つの要素レーンに出す中身。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ④PJ の変更一覧の要素 5 レーンの中身（ステージ・業務フロー・業務・業務ルール・画面）
 * @graph-connects none
 */
export interface PjLanes {
  readonly stages: readonly PjStageItem[];
  readonly flows: readonly PjFlowItem[];
  readonly businesses: readonly LaneBusiness[];
  readonly rules: readonly LaneRule[];
  readonly screens: readonly PjScreenItem[];
}

/**
 * `selectPjLanes` の入力。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 5 レーンの中身を決めるための入力（2 つの姿・索引・案件で変わった画面・要求・選択）
 * @graph-connects none
 */
export interface PjLanesInput {
  /** 案件を当てた姿。案件が作った要素はこちらに居る */
  readonly after: PjLaneView;
  /** master の姿。案件が消した要素はこちらにしか居ない */
  readonly master: PjLaneView;
  /** 型ごとの（id → 印）の索引（直接の変更だけ） */
  readonly touched: PjTouchedByType;
  /** 案件で変わった画面（名前つき） */
  readonly screens: readonly PjScreenItem[];
  /** その案件が持つ要求 */
  readonly requirements: readonly PjRequirement[];
  /** いまの選択 */
  readonly selection: PjLanesSelection;
}

/**
 * 触った要素を、案件を当てた姿 → master の姿 → どちらにも無い、の順で拾う。
 *
 * **どちらにも無い id もカードにする** — 変更の行が在るのに姿から引けないのは読取のずれであって、
 * 黙って落とすと画面の件数と案件の変更の行数が理由の分からないまま食い違う。
 *
 * @param ids 触ったノードの id（行の順）
 * @param after 案件を当てた姿の要素
 * @param master master の姿の要素
 * @param idOf 要素から id を取り出す
 * @param missing どちらの姿にも無い id から要素を組む
 * @returns レーンに出す要素
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 触った要素を、案件を当てた姿・master の姿・どちらにも無い id の順で集める。
 * @graph-connects none
 */
function touchedElements<T>(
  ids: ReadonlySet<number>,
  after: readonly T[],
  master: readonly T[],
  idOf: (item: T) => number,
  missing: (id: number) => T,
): readonly T[] {
  const picked = new Map<number, T>();
  for (const item of [...after, ...master]) {
    const id = idOf(item);
    if (ids.has(id) && !picked.has(id)) picked.set(id, item);
  }
  for (const id of ids) {
    if (!picked.has(id)) picked.set(id, missing(id));
  }
  return [...picked.values()];
}

/**
 * 業務フローの id から属するステージを引く表を、2 つの姿から作る。
 *
 * master の姿も混ぜるのは、案件が消した業務フローを押したときにステージが引けるようにする
 * ためである。案件を当てた姿が在ればそちらを優先する。
 *
 * @param input 2 つの姿
 * @returns 業務フローの id → 属するステージの id（属していなければ `null`）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件を当てた姿と master の姿から、業務フローの id で属するステージを引く表を作る。
 * @graph-connects none
 */
export function pjStageOfFlowIndex(
  input: Pick<PjLanesInput, 'after' | 'master'>,
): ReadonlyMap<number, number | null> {
  const index = new Map<number, number | null>();
  for (const flow of [...input.master.flows, ...input.after.flows]) {
    index.set(flow.flowId, flow.stageId);
  }
  return index;
}

/**
 * 画面のレーンに選択を当てる。
 *
 * 要求は横断の条件で、その要求が結ぶ画面か、要求が結ぶ部品を持つ画面だけを残す。階層は一番右の
 * 選択が効く — 業務を選べばその業務を操作する部品を持つ画面、業務フローを選べばその業務フローの
 * 業務のどれかを操作する画面、ステージだけを選べばそのステージに属する画面（属し方は読取が画面の
 * 導出で決めたもの）になる。
 *
 * @param screens 案件で変わった画面
 * @param linked 選んだ要求が結ぶノードの id。要求を選んでいなければ null
 * @param selection いまの選択
 * @param flows いまの業務フローのレーンの中身（業務フローの業務を引く）
 * @returns 画面のレーンに出す中身
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面のレーンを、要求が結ぶ画面・部品と、選んだ業務・業務フロー・ステージで絞る。
 * @graph-connects none
 */
function selectPjScreens(
  screens: readonly PjScreenItem[],
  linked: ReadonlySet<number> | null,
  selection: PjLanesSelection,
  flows: readonly PjFlowItem[],
): readonly PjScreenItem[] {
  const reqScreens =
    linked === null
      ? screens
      : screens.filter(
          (screen) =>
            linked.has(screen.screenId) || screen.componentIds.some((id) => linked.has(id)),
        );
  const { businessId, flowId, stageId } = selection;
  if (businessId !== undefined) {
    return reqScreens.filter((screen) => screen.businessIds.includes(businessId));
  }
  if (flowId !== undefined) {
    const flowBusinessIds = new Set(
      flows.filter((flow) => flow.flowId === flowId).flatMap((flow) => flow.businessIds),
    );
    return reqScreens.filter((screen) => screen.businessIds.some((id) => flowBusinessIds.has(id)));
  }
  if (stageId !== undefined) {
    return reqScreens.filter((screen) => screen.stageIds.includes(stageId));
  }
  return reqScreens;
}

/**
 * 選択を当てた 5 レーンの中身を作る。
 *
 * 要求は横断の条件で、**その要求が結ぶ変更と、その親（業務フロー・ステージ）だけ**に絞る。
 * 業務と業務ルールは要求が結ぶものだけ、業務フローは要求が結ぶか残った業務を含むもの、ステージは
 * 要求が結ぶか残った業務フローを含むものを残す。階層は左から右へ効く — ステージを選べば業務
 * フローはその配下だけ、業務フローを選べば業務はその業務フローが含むものだけ、業務を選べば業務
 * ルールはその業務のものだけになる。画面のレーンは業務ルールの右に並ぶが、業務ルールでは絞らない
 * （業務ルールのカードは選択を持たず、押すと業務ルールの画面へ移る）。絞り方は `selectPjScreens` が持つ。
 *
 * @param input 2 つの姿・索引・案件で変わった画面・要求・選択
 * @returns 5 レーンに出す中身
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選択を当てて、案件が触った要素だけの 5 レーンの中身を決める。
 * @graph-connects touchedElements [calls] 触った要素の集め方
 * @graph-connects selectPjScreens [calls] 画面のレーンの絞り込み
 */
export function selectPjLanes(input: PjLanesInput): PjLanes {
  const { after, master, touched, requirements, selection } = input;

  const requirement = requirements.find((item) => item.requirementId === selection.requirementId);
  const linked = requirement === undefined ? null : new Set(requirement.nodeIds);

  const touchedBusinesses = touchedElements(
    pjTouchedIdsOf(touched, 'Business'),
    after.businesses,
    master.businesses,
    (item) => item.businessId,
    (id) => ({
      businessId: id,
      label: '',
      untranslated: false,
      domain: null,
      actor: null,
      ruleIds: [],
    }),
  );
  const touchedRules = touchedElements(
    pjTouchedIdsOf(touched, 'Rule'),
    after.rules,
    master.rules,
    (item) => item.ruleId,
    (id) => ({
      ruleId: id,
      label: '',
      untranslated: false,
      kind: null,
      implementation: [],
    }),
  );
  const touchedFlows = touchedElements(
    pjTouchedIdsOf(touched, 'Flow'),
    after.flows,
    master.flows,
    (item) => item.flowId,
    (id) => ({
      flowId: id,
      stageId: null,
      label: '',
      untranslated: false,
      role: null,
      businessIds: [],
    }),
  );
  const touchedStages = touchedElements(
    pjTouchedIdsOf(touched, 'Stage'),
    after.stages,
    master.stages,
    (item) => item.stageId,
    (id) => ({ stageId: id, label: '', untranslated: false, flowIds: [] }),
  );

  const reqBusinesses =
    linked === null ? touchedBusinesses : touchedBusinesses.filter((b) => linked.has(b.businessId));
  const reqRules =
    linked === null ? touchedRules : touchedRules.filter((r) => linked.has(r.ruleId));
  const reqBusinessIds = new Set(reqBusinesses.map((b) => b.businessId));
  const reqFlows =
    linked === null
      ? touchedFlows
      : touchedFlows.filter(
          (flow) =>
            linked.has(flow.flowId) || flow.businessIds.some((id) => reqBusinessIds.has(id)),
        );
  const reqFlowIds = new Set(reqFlows.map((flow) => flow.flowId));
  const stages =
    linked === null
      ? touchedStages
      : touchedStages.filter(
          (stage) =>
            linked.has(stage.stageId) ||
            stage.flowIds.some((id) => reqFlowIds.has(id)) ||
            reqFlows.some((flow) => flow.stageId === stage.stageId),
        );

  const flows =
    selection.stageId === undefined
      ? reqFlows
      : reqFlows.filter((flow) => flow.stageId === selection.stageId);

  const flowScope =
    selection.flowId === undefined
      ? selection.stageId === undefined
        ? null
        : flows
      : flows.filter((flow) => flow.flowId === selection.flowId);
  const businessScope =
    flowScope === null ? null : new Set(flowScope.flatMap((flow) => flow.businessIds));
  const businesses =
    businessScope === null
      ? reqBusinesses
      : reqBusinesses.filter((business) => businessScope.has(business.businessId));

  const ruleOwners =
    selection.businessId === undefined
      ? businesses
      : businesses.filter((business) => business.businessId === selection.businessId);
  const ruleScope =
    businessScope === null && selection.businessId === undefined
      ? null
      : new Set(ruleOwners.flatMap((business) => business.ruleIds));
  const rules =
    ruleScope === null ? reqRules : reqRules.filter((rule) => ruleScope.has(rule.ruleId));
  const screens = selectPjScreens(input.screens, linked, selection, flows);

  return { stages, flows, businesses, rules, screens };
}
